import { FC, useEffect } from 'react'
import classNames from 'classnames'
import { Link2Icon, MoreHorizontalIcon } from 'lucide-react'
import { disablePageScroll, enablePageScroll } from 'scroll-lock'
import { FacebookIcon, InstagramIcon, LinkedInIcon, MessagesIcon, XIcon } from '../../assets'
import { useAppSelector } from '../../redux'
import { copyToClipboard, getOptimizedImage, useSegment } from '../../utils'
import { StyledShareDialog } from '.'

type Props = {
  onClose: () => void
}

const PLATFORMS = [
  {
    _id: 'copy',
    label: 'Copy',
    icon: <Link2Icon style={{ transform: 'rotate(-45deg)' }} />
  },
  // {
  //   _id: 'messages',
  //   label: 'Messages',
  //   icon: <MessagesIcon />,
  //   social: true,
  //   mobile: true
  // },
  // {
  //   _id: 'instagram',
  //   label: 'Instagram',
  //   icon: <InstagramIcon />,
  //   social: true
  // },
  {
    _id: 'facebook',
    label: 'Facebook',
    icon: <FacebookIcon />,
    social: true
  },
  {
    _id: 'linkedin',
    label: 'LinkedIn',
    icon: <LinkedInIcon />,
    social: true
  },
  {
    _id: 'x',
    label: 'X',
    icon: <XIcon />,
    social: true
  },
  {
    _id: 'more',
    label: 'More',
    icon: <MoreHorizontalIcon />
  }
]

const ShareDialog: FC<Props> = ({ onClose }) => {
  const { track } = useSegment()
  const { course } = useAppSelector((state) => state.course)

  const handleShare = async (platform: string) => {
    const title = encodeURIComponent(course?.title || '')
    const url = encodeURIComponent(document.location.href)

    if (platform === 'copy') {
      return await copyToClipboard(document.location.href)
    }

    if (platform === 'more') {
      return await navigator.share({
        url: document.location.href,
        text: `${course?.title}`
      })
    }

    if (platform === 'x') {
      return window.open(`https://twitter.com/intent/tweet?text=${title}&url=${url}`)
    }

    if (platform === 'linkedin') {
      return window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`)
    }

    if (platform === 'facebook') {
      return window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`)
    }
  }

  useEffect(() => {
    disablePageScroll()

    return () => {
      enablePageScroll()
    }
  }, [])

  useEffect(() => {
    if (!course) return

    track('Opened Course Share Window', {
      course_id: course._id,
      course_title: course.title
    })
  }, [course])

  return (
    <StyledShareDialog>
      <div className="backdrop" onClick={() => onClose()} />
      <div className="dialog">
        <div className="course-details-container">
          <div className="course-preview">
            {course?.coverImage?.fileHttpLink && (
              <img src={getOptimizedImage(course.coverImage.fileHttpLink, { width: 500 })} alt="" />
            )}
          </div>
          <div className="course-details">
            <span className="course-title">{course?.title}</span>
            <span className="course-description">
              {course?.previewDescription || course?.description}
            </span>
          </div>
        </div>
        <div className="share-container">
          <div className="share-actions">
            {PLATFORMS.map((platform) => {
              if (platform._id === 'more' && (!navigator?.share || !navigator?.canShare())) {
                return
              }

              return (
                <button
                  className={classNames({ 'icon-btn': !platform.social })}
                  data-platform={platform._id}
                  key={platform._id}
                  onClick={() => handleShare(platform._id)}
                >
                  <div className="icon">{platform.icon}</div>
                  <span className="label">{platform.label}</span>
                </button>
              )
            })}
          </div>
        </div>
      </div>
    </StyledShareDialog>
  )
}

export default ShareDialog
